/**
 * Pose viewer (`?scene=poses`): every animation, from eight directions, under the real
 * pipeline at a closer zoom. Rows top to bottom: idle, walk, run, walk covering a flank
 * (twist), backpedal, aim, crawl, dead. Characters animate on the spot.
 *
 *   &only=<row>   one row alone - the rest are listed in ROWS by index (the stab, the lunge, is 11;
 *                 the sneak rows of ROUND7 are 19-23)
 *   &ghillie=1    every ranger in the ghillie wrap      &biome=  &ppm=  &pitch=  &freeze=1&t=
 *   &dog=1        the tracker dog instead (ROUND8 §3), rows in DOG_ROWS: heel, cast, track, bay, mourn, the fall.
 *                 Casting (nose down, rows 3-5) against baying (head up, rows 6-7) is the tell to judge.
 */

import { Color, DirectionalLight, HemisphereLight, type Material, Scene, type WebGLRenderer } from 'three';
import { PixelCamera } from '../render/camera/PixelCamera';
import { Dog, type DogHead, type DogMotion } from '../render/characters/dog';
import { DOG_DEATH_SECONDS } from '../render/characters/dogPoses';
import { MOTION_SECONDS } from '../render/characters/poses';
import { type Motion, Soldier } from '../render/characters/soldier';
import { createGradientMap, createToonMaterial, OUTLINE, sharedUniforms } from '../render/materials/toon';
import { type BiomeName, getGrade, Palette } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { buildTerrain } from '../render/terrain/Terrain';
import { DOGS } from '../sim/tuningDogs';
import { num, pick, query } from './params';

interface Row {
  motion: Motion;
  speed: number;
  twist: number;
  reverse: boolean;
  /** Walking with his hands up: a prisoner being led. */
  handsUp?: boolean;
}

const ROWS: readonly Row[] = [
  { motion: 'idle', speed: 0, twist: 0, reverse: false },
  { motion: 'walk', speed: 2.2, twist: 0, reverse: false },
  { motion: 'run', speed: 4.6, twist: 0, reverse: false },
  { motion: 'walk', speed: 1.9, twist: 1.3, reverse: false },
  { motion: 'walk', speed: 1.3, twist: -0.5, reverse: true },
  { motion: 'aim', speed: 0, twist: 0.6, reverse: false },
  { motion: 'crawl', speed: 0.9, twist: 0, reverse: false },
  { motion: 'dead', speed: 0, twist: 0, reverse: false },
  // One-shot actions and states: twenty-four rows do not fit a screen, so view these with &only=<row>.
  { motion: 'fire', speed: 0, twist: 0, reverse: false },
  { motion: 'fireProne', speed: 0, twist: 0, reverse: false },
  { motion: 'throw', speed: 0, twist: 0, reverse: false },
  { motion: 'stab', speed: 0, twist: 0, reverse: false },
  { motion: 'hit', speed: 0, twist: 0, reverse: false },
  { motion: 'deathCrumple', speed: 0, twist: 0, reverse: false },
  { motion: 'deathBlast', speed: 0, twist: 0, reverse: false },
  { motion: 'wounded', speed: 0, twist: 0, reverse: false },
  { motion: 'surrender', speed: 0, twist: 0, reverse: false },
  { motion: 'walk', speed: 1.9, twist: 0, reverse: false, handsUp: true },
  { motion: 'board', speed: 0, twist: 0, reverse: false },
  // ROUND7, sneaking (19-23): the crouched walk, covering a flank, backing away; the crouch; the crouched aim.
  { motion: 'sneak', speed: 1.3, twist: 0, reverse: false },
  { motion: 'sneak', speed: 1.1, twist: 1.3, reverse: false },
  { motion: 'sneak', speed: 0.9, twist: -0.5, reverse: true },
  { motion: 'crouch', speed: 0, twist: 0, reverse: false },
  { motion: 'crouchAim', speed: 0, twist: 0.3, reverse: false },
];
const COLUMNS = 8;

/** The dog's rows, each at the speed the sim moves it in that mode (tuningDogs.ts). */
const DOG_ROWS: ReadonlyArray<{ motion: DogMotion; head: DogHead; speed: number }> = [
  { motion: 'stand', head: 'alert', speed: 0 }, // 0-2 heel: standing, walking, keeping up with a runner
  { motion: 'walk', head: 'alert', speed: 1.5 },
  { motion: 'trot', head: 'alert', speed: DOGS.trackSpeed },
  { motion: 'stand', head: 'sniff', speed: 0 }, // 3-5 cast, standing and walking; track, on a hot trail
  { motion: 'walk', head: 'sniff', speed: DOGS.castSpeed },
  { motion: 'trot', head: 'sniff', speed: DOGS.trackSpeed },
  { motion: 'stand', head: 'bark', speed: 0 }, // 6-7 bay: square to him, and following him
  { motion: 'walk', head: 'bark', speed: 1.5 },
  { motion: 'sit', head: 'alert', speed: 0 }, // 8 heel, the handler standing a while
  { motion: 'sit', head: 'bark', speed: 0 }, // 9 mourn, by its handler's body
  { motion: 'death', head: 'alert', speed: 0 }, // 10-11 the fall, and the body
  { motion: 'dead', head: 'alert', speed: 0 },
];

/** The dog rows, from eight directions: small beside the men, so packed closer. */
function dogCast(palette: Palette, material: Material, scene: Scene, only: number): Array<{ dog: Dog; speed: number }> {
  const shown = only >= 0 ? DOG_ROWS.filter((_, i) => i === only) : DOG_ROWS;
  const cast: Array<{ dog: Dog; speed: number }> = [];
  shown.forEach((row, r) => {
    for (let c = 0; c < COLUMNS; c++) {
      const dog = new Dog(palette, material);
      dog.state.position.set((c - (COLUMNS - 1) / 2) * 1.9, 0, (r - (shown.length - 1) / 2) * 1.35);
      dog.state.facing = (c / COLUMNS) * Math.PI * 2;
      dog.state.motion = row.motion;
      dog.state.head = row.head;
      scene.add(dog.mesh);
      cast.push({ dog, speed: row.speed });
    }
  });
  return cast;
}

export function startPoses(renderer: WebGLRenderer): void {
  const biome = pick<BiomeName>('biome', ['arctic', 'desert', 'temperate'], 'temperate');
  const palette = new Palette(biome);
  const grade = getGrade('day');
  const gradient = createGradientMap();
  const scene = new Scene();
  const camera = new PixelCamera(num('ppm', 30), num('pitch', 45));

  const sun = new DirectionalLight(new Color(grade.sunColor), grade.sunIntensity);
  sun.position.set(grade.sunDir[0] * 80, grade.sunDir[1] * 80, grade.sunDir[2] * 80);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, { left: -16, right: 16, top: 16, bottom: -16, near: 10, far: 200 });
  sun.shadow.bias = -0.0006;
  scene.add(sun, sun.target, new HemisphereLight(new Color(grade.skyColor), new Color(grade.groundColor), grade.hemiIntensity));

  scene.add(buildTerrain({
    originX: -20, originZ: -16, sizeX: 40, sizeZ: 32,
    heightAt: () => 0,
    materialAt: () => [1, 0, 0, 0],
    ramps: ['ground', 'groundAlt', 'dirt', 'rock'],
    baseSteps: biome === 'arctic' ? [3, 2, 1, 1] : [2, 1, 1, 1],
  }, palette, gradient).ground);

  const playerMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.player });
  const enemyMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.character });
  const cast: Array<{ soldier: Soldier; row: Row }> = [];
  // &only=<row index> isolates one animation for a close look.
  const only = num('only', -1);
  const ghillie = query.get('ghillie') === '1';
  const dogs = query.get('dog') === '1' ? dogCast(palette, enemyMaterial, scene, only) : [];
  const shown = dogs.length > 0 ? [] : only >= 0 ? ROWS.filter((_, i) => i === only) : ROWS;
  shown.forEach((row, r) => {
    for (let c = 0; c < COLUMNS; c++) {
      // Alternate ranger and enemy so both uniforms are checked in every pose.
      const ranger = (r + c) % 2 === 0;
      const soldier = new Soldier(palette, ranger ? playerMaterial : enemyMaterial, ranger
        ? { side: 'ranger', skin: 'skinB', headgear: 'helmet', pack: true, ghillie }
        : { side: 'enemy', skin: 'skinA', headgear: 'helmet', pack: false });
      soldier.state.position.set((c - (COLUMNS - 1) / 2) * 2.45, 0, (r - (shown.length - 1) / 2) * 3.1);
      soldier.state.facing = (c / COLUMNS) * Math.PI * 2;
      soldier.state.motion = row.motion;
      soldier.state.twist = row.twist;
      soldier.state.reverse = row.reverse;
      soldier.state.handsUp = row.handsUp ?? false;
      scene.add(soldier.mesh);
      cast.push({ soldier, row });
    }
  });

  const pipeline = new RenderPipeline(renderer, palette.buildLut(), palette.color('ink.0'));
  pipeline.setGrade({ ...grade, hazeAmount: 0 }, palette.haze);
  const frozen = query.get('freeze') === '1';
  const start = performance.now();

  function frame(now: number): void {
    const time = frozen ? num('t', 0) : (now - start) / 1000;
    sharedUniforms.uTime.value = time;
    camera.update();
    for (const { soldier, row } of cast) {
      soldier.state.travelled = time * row.speed;
      // One-shots loop with a beat of rest between, so the whole action can be watched repeatedly.
      const length = MOTION_SECONDS[row.motion];
      soldier.state.age = length > 0 ? time % (length + 0.6) : time;
      soldier.update(time, camera);
    }
    for (const { dog, speed } of dogs) {
      dog.state.travelled = time * speed;
      // The fall loops with a beat of rest; the jaw snaps at the sim's bark rate.
      dog.state.age = time % (DOG_DEATH_SECONDS + 0.6);
      dog.state.sinceBark = time % DOGS.barkEverySeconds;
      dog.update(time, camera);
    }
    renderer.info.reset();
    pipeline.render(scene, camera);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
